import { Box, Skeleton, Tooltip } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
// eslint-disable-next-line
import Select, { SelectChangeEvent } from '@mui/material/Select';
import NoData from 'components/elements/Reports/NoData';
import Top6PieChart, {
  PIE_CHART_HEIGHT,
  PieChartData,
  sliceColours
} from 'components/structure/Analytics/Top6PieChart';
import Logout from 'components/structure/Logout/Logout';
import { LiveSpaceUtilizationTable } from 'components/structure/Space/SpaceAnalytics/LiveSpaceUtilizationTable';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import SpaceService, { LiveOccupancyResponse, SpaceProps } from 'services/hooks/space-service';
import { containerStyle, liveIndicatorStyle } from 'utils/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const LS_KEY_BUILDING = 'bl.spaces.live.building';

const transformArrayToObject = (array: LiveOccupancyResponse[]): LiveOccupancyResponse => {
  const result: LiveOccupancyResponse = {};

  if (!!array && !!array.length) {
    array.forEach((obj) => {
      for (const [uuid, value] of Object.entries(obj)) {
        if (result[uuid]) {
          result[uuid] += value; // Assuming a sum operation for duplicate UUIDs
        } else {
          result[uuid] = value;
        }
      }
    });
  }

  return result;
};

export type RelativeLiveOccupancy = {
  uuid: string;
  name: string;
  occupants: number;
  relative: number;
  capacity: number;
};

function getRelativeLiveData(
  spaces: SpaceProps[],
  liveData: [string, number][]
): RelativeLiveOccupancy[] {
  const liveDataMap = new Map<string, number>(liveData);

  const tempArray: RelativeLiveOccupancy[] = spaces
    .map((space) => {
      const occupants = space.uuid ? liveDataMap.get(space.uuid) || 0 : 0;
      const capacity = Number(space.capacity);
      return {
        uuid: space.uuid || '', // Fallback to empty string if uuid is undefined
        name: space.name,
        occupants,
        relative: !isNaN(capacity) && capacity > 0 ? occupants / capacity : 0,
        capacity: capacity
      };
    })
    .filter((item) => item.uuid !== '' && !isNaN(item.capacity) && item.capacity > 0) // Filter out items with no uuid or invalid capacity
    .sort((a, b) => b.relative - a.relative); // Sort by 'relative' in descending order

  return tempArray;
}

function getLiveSpaceUtilizationData(relativeOccupancy: RelativeLiveOccupancy[]) {
  return relativeOccupancy.map((val) => ({
    name: val.name,
    occupancy: val.relative,
    occupants: val.occupants,
    capacity: val.capacity
  }));
}

function generatePieChartData(data: RelativeLiveOccupancy[]): PieChartData {
  // Step 1: Sort the data by occupants in descending order
  const sortedData = [...data].sort((a, b) => b.occupants - a.occupants);

  // Step 2: Slice the array to keep only the top 6
  const topSix = sortedData.slice(0, 6);

  // Step 3: Reduce the rest, if any, to an "Other" category
  const otherOccupants = sortedData.slice(6).reduce((acc, curr) => acc + curr.occupants, 0);

  // Step 4: Transform the topSix into the expected format, add "Other" if necessary
  const transformedData = topSix.map((item) => ({
    id: item.uuid,
    value: item.occupants,
    label: item.name
  }));

  if (otherOccupants > 0) {
    transformedData.push({
      id: 'Other',
      value: otherOccupants,
      label: 'Other spaces'
    });
  }

  return transformedData;
}

export default function SpaceLive() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // get last selected building from localstorage
  const lsSelectedBuildingUuid = localStorage.getItem(LS_KEY_BUILDING);

  const [spacesInBuilding, setSpacesInBuilding] = useState<SpaceProps[]>([]);
  const [selectedBuildingUuid, setSelectedBuildingUuid] = useState<string>('');

  // Buildings query
  const { isLoading: isBuildingsLoading, data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error('Problem fetching buildings.');
      }
    }
  );

  // Spaces query
  const { isLoading: isAllSpacesLoading, data: allSpacesData } = useQuery<SpaceProps[], Error>(
    'allSpaces',
    SpaceService.findAllSpaces,
    {
      onError: () => {
        toast.error('Problem fetching spaces.');
      }
    }
  );

  const THIRTY_SECONDS_MS = 30000;

  // Live Occupancy query
  const { isLoading: isLiveOccupancyLoading, data: liveOccupancyData } = useQuery<
    LiveOccupancyResponse[],
    Error
  >(
    ['liveOccupancy', selectedBuildingUuid],
    () =>
      SpaceService.liveOccupancy({
        buildingUuid: selectedBuildingUuid
      }),
    {
      enabled: !!selectedBuildingUuid,
      refetchInterval: THIRTY_SECONDS_MS,
      onError: () => {
        toast.error('Problem fetching occupancy data.');
      }
    }
  );

  const liveSpaceData = transformArrayToObject(liveOccupancyData ?? []);
  const sortedLiveData = Object.entries(liveSpaceData ?? {}).sort((a, b) => b[1] - a[1]);
  const relativeLiveData = getRelativeLiveData(spacesInBuilding, sortedLiveData);
  const pieChartData = generatePieChartData(relativeLiveData);
  const liveSpaceRows = getLiveSpaceUtilizationData(relativeLiveData);

  useEffect(() => {
    if (!!selectedBuildingUuid && !!allSpacesData) {
      // filter the spaces that only apply to the building and store them in the state
      setSpacesInBuilding(
        allSpacesData.filter((space) => space.buildingUuid === selectedBuildingUuid)
      );
    }
  }, [selectedBuildingUuid, allSpacesData]);

  /**
   * useEffect to restore the last selections from localstorage.
   */
  useEffect(() => {
    // If no building is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!buildingsData &&
      !selectedBuildingUuid &&
      lsSelectedBuildingUuid &&
      buildingsData.find((building) => building.uuid === lsSelectedBuildingUuid)
    ) {
      setSelectedBuildingUuid(lsSelectedBuildingUuid);
    }
  }, [buildingsData, lsSelectedBuildingUuid, selectedBuildingUuid]);

  const handleBuildingChange = (event: SelectChangeEvent) => {
    const building = event.target.value as string;
    setSelectedBuildingUuid(building);
    // store the last selected building in localstorage
    localStorage.setItem(LS_KEY_BUILDING, building);
  };

  const isLoading = isBuildingsLoading || isAllSpacesLoading || isLiveOccupancyLoading;
  const totalOccupants = pieChartData.reduce((prev, current) => prev + current.value, 0);

  return (
    <div style={{ marginTop: 40 }}>
      <Grid container justifyContent="right" style={{ paddingBottom: 20 }}>
        <Grid item>
          <Logout />
        </Grid>
      </Grid>

      <span>
        <h1 style={{ paddingBottom: 20 }}>
          Spaces{' '}
          <span style={liveIndicatorStyle}>
            <Tooltip title={'Refreshes every 30 seconds.'}>
              <span>● Live Data</span>
            </Tooltip>
          </span>
        </h1>
      </span>

      <Grid container justifyContent="right" style={{ paddingBottom: 20 }}>
        <Grid item style={{ width: 300, marginLeft: 10 }}>
          <FormControl fullWidth size="small" disabled={isBuildingsLoading}>
            <InputLabel>Building</InputLabel>
            <Select
              value={selectedBuildingUuid}
              sx={{ borderRadius: '10px', backgroundColor: 'white' }}
              disabled={!buildingsData}
              onChange={handleBuildingChange}
              label="Building"
            >
              {!!buildingsData &&
                buildingsData
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((building) => {
                    return (
                      <MenuItem key={building.uuid} value={building.uuid}>
                        {building.name}
                      </MenuItem>
                    );
                  })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <div style={containerStyle}>
        <h4 style={{ margin: 10, paddingBottom: 10 }}>Most Utilized Spaces</h4>
        {isLoading ? (
          <Skeleton variant="rectangular" height={150} style={{ margin: 10 }} />
        ) : (
          <>
            {!!relativeLiveData && !!relativeLiveData.length ? (
              <Grid container spacing={2} style={{ paddingLeft: 15 }}>
                {relativeLiveData.slice(0, 6).map((space) => {
                  return (
                    <Grid item xs={4} md={3} lg={2} style={{ padding: 10 }} key={space.uuid}>
                      <Box style={space.relative > 1 ? { color: '#FA6B5B' } : {}}>
                        <div style={{ fontSize: '16px' }}>{space.name}</div>
                        <div style={{ fontWeight: 'bold', fontSize: '40px' }}>
                          {Math.round(space.relative * 100)}%
                        </div>
                        <div style={{ fontSize: '16px', marginTop: -10 }}>
                          {Math.round(space.occupants)}/{space.capacity}
                        </div>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <NoData height={150} hint={'Consider updating your building filter'} />
            )}
          </>
        )}
      </div>

      <Grid container wrap={isDesktop ? 'nowrap' : 'wrap'} gap={2} style={{ width: '100%' }}>
        <Grid item xs={12} md={6} style={containerStyle}>
          <h4 style={{ margin: 10 }}>Current Space Utilization</h4>
          <LiveSpaceUtilizationTable
            liveSpaceRows={liveSpaceRows}
            loading={isLoading}
            showOccupancy={isDesktop}
          />
        </Grid>
        <Grid item xs={12} md={6} style={containerStyle}>
          <h4 style={{ margin: 10 }}>Current Occupants Breakdown</h4>
          <Grid container>
            <Grid item xs={12} md={9}>
              <div style={{ width: '99%', marginLeft: 10 }}>
                {isLoading || totalOccupants > 0 ? (
                  <Top6PieChart data={pieChartData} loading={isLoading} />
                ) : (
                  <NoData height={PIE_CHART_HEIGHT} hint="No occupants in building" />
                )}
              </div>
            </Grid>
            <Grid item xs={12} md={3}>
              {isLoading ? (
                <Skeleton variant="rectangular" height={600} style={{ marginLeft: 20 }} />
              ) : (
                !!pieChartData &&
                !!pieChartData.length && (
                  <>
                    <div style={{ paddingBottom: 15 }}>
                      <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{totalOccupants}</div>
                      <div style={{ marginTop: -5, fontSize: '14px' }}>In Building</div>
                    </div>

                    {pieChartData.map((slice, index) => {
                      return (
                        <div key={slice.id} style={{ paddingBottom: 15 }}>
                          <div
                            style={{
                              color: sliceColours[index],
                              fontSize: '22px',
                              fontWeight: 'bold'
                            }}
                          >
                            {slice.value}
                          </div>
                          <div style={{ marginTop: -5, fontSize: '14px' }}>{slice.label}</div>
                        </div>
                      );
                    })}
                  </>
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}
